// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/services/fiscalService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { PaginadoResponse } from '@/features/financeiro/types';
import type {
  CBenefListagemDto, CBenefDto, SalvarCBenefRequest,
  CestListagemDto, CestComboDto, CestDto, SalvarCestRequest, ImportarCestResultado,
  CsosnComboDto, CsosnListagemDto, CsosnDto, SalvarCsosnRequest,
  CfopListagemDto, CfopComboDto, CfopDto, SalvarCfopRequest,
  CstIcmsListagemDto, CstIcmsComboDto, CstIcmsDto, SalvarCstIcmsRequest,
  CstPisCofinsListagemDto, CstPisCofinsComboDto,
  NaturezaOperacaoListagemDto, NaturezaOperacaoComboDto,
  NaturezaOperacaoDto, SalvarNaturezaOperacaoRequest,
  RegraFiscalListagemDto, RegraFiscalDto, SalvarRegraFiscalRequest,
  CClassTribListagemDto, CClassTribComboDto, CClassTribDto, SalvarCClassTribRequest,
  CorrelacaoCfopCstListagemDto, CorrelacaoCfopCstDto, SalvarCorrelacaoCfopCstRequest,
  MonofasicoListagemDto, MonofasicoDto, SalvarMonofasicoRequest,
  TabUfIcmsDto, SalvarTabUfIcmsRequest,
  AnpListagemDto, AnpDto, SalvarAnpRequest,
  NcmIcmsPartilhaListagemDto, NcmIcmsPartilhaDto, SalvarNcmIcmsPartilhaRequest,
  NcmListagemDto, NcmDto, SalvarNcmRequest, ImportarNcmResultado,
  ClassificacaoFiscalListagemDto, ClassificacaoFiscalDto, SalvarClassificacaoFiscalRequest,
} from '../types';

// ── CEST ──────────────────────────────────────────────────────────────────

export const cestService = {
  combo: (busca?: string) =>
    apiClient.get<CestComboDto[]>('/api/cest', { params: { busca } }).then(r => r.data),

  listar: (params?: { busca?: string; segmento?: string; pagina?: number; tamanhoPagina?: number }) =>
    apiClient
      .get<PaginadoResponse<CestListagemDto>>('/api/cadastros/fiscal/cest', { params })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<CestDto>(`/api/cadastros/fiscal/cest/${id}`).then(r => r.data),

  criar: (req: SalvarCestRequest) =>
    apiClient.post<CestDto>('/api/cadastros/fiscal/cest', req).then(r => r.data),

  atualizar: (id: number, req: SalvarCestRequest) =>
    apiClient.put(`/api/cadastros/fiscal/cest/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/cest/${id}`),

  importar: (arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return apiClient
      .post<ImportarCestResultado>('/api/cadastros/fiscal/cest/importar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data);
  },
};

// ── CBenef ────────────────────────────────────────────────────────────────

export const cbenefService = {
  listar: (params?: { busca?: string; produtoId?: number; uf?: string; pagina?: number; tamanhoPagina?: number }) =>
    apiClient
      .get<PaginadoResponse<CBenefListagemDto>>('/api/cadastros/fiscal/cbenef', { params })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<CBenefDto>(`/api/cadastros/fiscal/cbenef/${id}`).then(r => r.data),

  criar: (req: SalvarCBenefRequest) =>
    apiClient.post<CBenefDto>('/api/cadastros/fiscal/cbenef', req).then(r => r.data),

  atualizar: (id: number, req: SalvarCBenefRequest) =>
    apiClient.put(`/api/cadastros/fiscal/cbenef/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/cbenef/${id}`),
};

// ── CFOP ──────────────────────────────────────────────────────────────────

export const cfopService = {
  combo: (busca?: string) =>
    apiClient.get<CfopComboDto[]>('/api/cfops', { params: { busca } }).then(r => r.data),

  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<CfopListagemDto>>('/api/cadastros/fiscal/cfops', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<CfopDto>(`/api/cadastros/fiscal/cfops/${id}`).then(r => r.data),

  criar: (req: SalvarCfopRequest) =>
    apiClient.post<CfopDto>('/api/cadastros/fiscal/cfops', req).then(r => r.data),

  atualizar: (id: number, req: SalvarCfopRequest) =>
    apiClient.put(`/api/cadastros/fiscal/cfops/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/cfops/${id}`),

  importar: (arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return apiClient
      .post<ImportarCestResultado>('/api/cadastros/fiscal/cfops/importar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data);
  },
};

// ── Tabelas de apoio (Origem da mercadoria, CST IPI) ──────────────────────
export interface ApoioComboDto { id: number; codigo: string | null; descricao: string | null; }

export const apoioService = {
  origens: () => apiClient.get<ApoioComboDto[]>('/api/cadastros/fiscal/origens').then(r => r.data),
  cstsIpi: () => apiClient.get<ApoioComboDto[]>('/api/cadastros/fiscal/csts-ipi').then(r => r.data),
  naturezasReceita: (busca?: string, id?: number) =>
    apiClient.get<ApoioComboDto[]>('/api/cadastros/fiscal/naturezas-receita', { params: { busca, id } }).then(r => r.data),
};

// ── CST ───────────────────────────────────────────────────────────────────

export const cstService = {
  comboCsosn: () =>
    apiClient.get<CsosnComboDto[]>('/api/csosn').then(r => r.data),

  comboIcms: () =>
    apiClient.get<CstIcmsComboDto[]>('/api/cst-icms').then(r => r.data),

  listarIcms: (busca?: string, pagina = 1, tamanhoPagina = 100) =>
    apiClient
      .get<PaginadoResponse<CstIcmsListagemDto>>('/api/cadastros/fiscal/cst-icms', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterIcmsPorId: (id: number) =>
    apiClient.get<CstIcmsDto>(`/api/cadastros/fiscal/cst-icms/${id}`).then(r => r.data),

  criarIcms: (req: SalvarCstIcmsRequest) =>
    apiClient.post<CstIcmsDto>('/api/cadastros/fiscal/cst-icms', req).then(r => r.data),

  atualizarIcms: (id: number, req: SalvarCstIcmsRequest) =>
    apiClient.put(`/api/cadastros/fiscal/cst-icms/${id}`, req),

  excluirIcms: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/cst-icms/${id}`),

  comboPisCofins: () =>
    apiClient.get<CstPisCofinsComboDto[]>('/api/cst-pis-cofins').then(r => r.data),

  listarPisCofins: (busca?: string, pagina = 1, tamanhoPagina = 100) =>
    apiClient
      .get<PaginadoResponse<CstPisCofinsListagemDto>>('/api/cadastros/fiscal/cst-pis-cofins', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  listarCsosn: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<CsosnListagemDto>>('/api/cadastros/fiscal/csosn', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterCsosnPorId: (id: number) =>
    apiClient.get<CsosnDto>(`/api/cadastros/fiscal/csosn/${id}`).then(r => r.data),

  criarCsosn: (req: SalvarCsosnRequest) =>
    apiClient.post<CsosnDto>('/api/cadastros/fiscal/csosn', req).then(r => r.data),

  atualizarCsosn: (id: number, req: SalvarCsosnRequest) =>
    apiClient.put(`/api/cadastros/fiscal/csosn/${id}`, req),

  excluirCsosn: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/csosn/${id}`),
};

// ── Natureza de Operação ──────────────────────────────────────────────────

export const naturezaOperacaoService = {
  combo: () =>
    apiClient.get<NaturezaOperacaoComboDto[]>('/api/naturezas-operacao').then(r => r.data),

  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<NaturezaOperacaoListagemDto>>('/api/cadastros/fiscal/naturezas-operacao', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient
      .get<NaturezaOperacaoDto>(`/api/cadastros/fiscal/naturezas-operacao/${id}`)
      .then(r => r.data),

  criar: (req: SalvarNaturezaOperacaoRequest) =>
    apiClient
      .post<NaturezaOperacaoDto>('/api/cadastros/fiscal/naturezas-operacao', req)
      .then(r => r.data),

  atualizar: (id: number, req: SalvarNaturezaOperacaoRequest) =>
    apiClient.put(`/api/cadastros/fiscal/naturezas-operacao/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/naturezas-operacao/${id}`),
};

// ── Regra Fiscal ──────────────────────────────────────────────────────────

export const regraFiscalService = {
  listar: (params?: { id?: number; ncmId?: number; cfopId?: number; ufDestino?: string; busca?: string; pagina?: number; tamanhoPagina?: number }) =>
    apiClient
      .get<PaginadoResponse<RegraFiscalListagemDto>>('/api/cadastros/fiscal/regras-fiscais', { params })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<RegraFiscalDto>(`/api/cadastros/fiscal/regras-fiscais/${id}`).then(r => r.data),

  criar: (req: SalvarRegraFiscalRequest) =>
    apiClient.post<RegraFiscalDto>('/api/cadastros/fiscal/regras-fiscais', req).then(r => r.data),

  atualizar: (id: number, req: SalvarRegraFiscalRequest) =>
    apiClient.put(`/api/cadastros/fiscal/regras-fiscais/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/regras-fiscais/${id}`),
};

// ── CClass Tributária ─────────────────────────────────────────────────────

export const cClassTribService = {
  combo: () =>
    apiClient.get<CClassTribComboDto[]>('/api/cclass-trib').then(r => r.data),

  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<CClassTribListagemDto>>('/api/cadastros/fiscal/cclass-trib', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<CClassTribDto>(`/api/cadastros/fiscal/cclass-trib/${id}`).then(r => r.data),

  criar: (req: SalvarCClassTribRequest) =>
    apiClient.post<CClassTribDto>('/api/cadastros/fiscal/cclass-trib', req).then(r => r.data),

  atualizar: (id: number, req: SalvarCClassTribRequest) =>
    apiClient.put(`/api/cadastros/fiscal/cclass-trib/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/cclass-trib/${id}`),
};

// ── Tabela UF ICMS ────────────────────────────────────────────────────────

export const tabUfIcmsService = {
  listar: () =>
    apiClient.get<TabUfIcmsDto[]>('/api/cadastros/fiscal/uf-icms').then(r => r.data),

  obterPorUf: (ufOrigem: string) =>
    apiClient.get<TabUfIcmsDto>(`/api/cadastros/fiscal/uf-icms/${ufOrigem}`).then(r => r.data),

  criar: (ufOrigem: string, req: SalvarTabUfIcmsRequest) =>
    apiClient.post<TabUfIcmsDto>(`/api/cadastros/fiscal/uf-icms/${ufOrigem}`, req).then(r => r.data),

  atualizar: (ufOrigem: string, req: SalvarTabUfIcmsRequest) =>
    apiClient.put(`/api/cadastros/fiscal/uf-icms/${ufOrigem}`, req),
};

// ── Monofásico PIS/COFINS ─────────────────────────────────────────────────

export const monofasicoService = {
  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<MonofasicoListagemDto>>('/api/cadastros/fiscal/monofasicos', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<MonofasicoDto>(`/api/cadastros/fiscal/monofasicos/${id}`).then(r => r.data),

  criar: (req: SalvarMonofasicoRequest) =>
    apiClient.post<MonofasicoDto>('/api/cadastros/fiscal/monofasicos', req).then(r => r.data),

  atualizar: (id: number, req: SalvarMonofasicoRequest) =>
    apiClient.put(`/api/cadastros/fiscal/monofasicos/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/monofasicos/${id}`),
};

// ── Correlação CFOP/CST ───────────────────────────────────────────────────

export const correlacaoService = {
  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<CorrelacaoCfopCstListagemDto>>('/api/cadastros/fiscal/correlacoes-cfop-cst', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient
      .get<CorrelacaoCfopCstDto>(`/api/cadastros/fiscal/correlacoes-cfop-cst/${id}`)
      .then(r => r.data),

  obterPorCst: (cst: string) =>
    apiClient
      .get<CorrelacaoCfopCstDto>(`/api/cadastros/fiscal/correlacoes-cfop-cst/por-cst/${cst}`)
      .then(r => r.data),

  criar: (req: SalvarCorrelacaoCfopCstRequest) =>
    apiClient
      .post<CorrelacaoCfopCstDto>('/api/cadastros/fiscal/correlacoes-cfop-cst', req)
      .then(r => r.data),

  atualizar: (id: number, req: SalvarCorrelacaoCfopCstRequest) =>
    apiClient.put(`/api/cadastros/fiscal/correlacoes-cfop-cst/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/correlacoes-cfop-cst/${id}`),
};

// ── ANP ───────────────────────────────────────────────────────────────────

export const anpService = {
  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<AnpListagemDto>>('/api/cadastros/fiscal/anp', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<AnpDto>(`/api/cadastros/fiscal/anp/${id}`).then(r => r.data),

  criar: (req: SalvarAnpRequest) =>
    apiClient.post<AnpDto>('/api/cadastros/fiscal/anp', req).then(r => r.data),

  atualizar: (id: number, req: SalvarAnpRequest) =>
    apiClient.put(`/api/cadastros/fiscal/anp/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/anp/${id}`),
};

// ── FCP — NcmIcmsPartilha ─────────────────────────────────────────────────

export const fcpService = {
  listar: (busca?: string, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<NcmIcmsPartilhaListagemDto>>('/api/cadastros/fiscal/fcp', {
        params: { busca, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient.get<NcmIcmsPartilhaDto>(`/api/cadastros/fiscal/fcp/${id}`).then(r => r.data),

  criar: (req: SalvarNcmIcmsPartilhaRequest) =>
    apiClient.post<NcmIcmsPartilhaDto>('/api/cadastros/fiscal/fcp', req).then(r => r.data),

  atualizar: (id: number, req: SalvarNcmIcmsPartilhaRequest) =>
    apiClient.put(`/api/cadastros/fiscal/fcp/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/fcp/${id}`),
};

// ── NCM ───────────────────────────────────────────────────────────────────

// ── Classificação Fiscal ──────────────────────────────────────────────────

export const classificacaoFiscalService = {
  listar: (params?: { ncmId?: number; busca?: string; pagina?: number; tamanhoPagina?: number }) =>
    apiClient
      .get<PaginadoResponse<ClassificacaoFiscalListagemDto>>(
        '/api/cadastros/fiscal/classificacoes-fiscais', { params })
      .then(r => r.data),

  obterPorId: (id: number) =>
    apiClient
      .get<ClassificacaoFiscalDto>(`/api/cadastros/fiscal/classificacoes-fiscais/${id}`)
      .then(r => r.data),

  criar: (req: SalvarClassificacaoFiscalRequest) =>
    apiClient
      .post<ClassificacaoFiscalDto>('/api/cadastros/fiscal/classificacoes-fiscais', req)
      .then(r => r.data),

  atualizar: (id: number, req: SalvarClassificacaoFiscalRequest) =>
    apiClient.put(`/api/cadastros/fiscal/classificacoes-fiscais/${id}`, req),

  excluir: (id: number) =>
    apiClient.delete(`/api/cadastros/fiscal/classificacoes-fiscais/${id}`),
};

export const ncmService = {
  listar: (busca?: string, ativo?: boolean | null, pagina = 1, tamanhoPagina = 50) =>
    apiClient
      .get<PaginadoResponse<NcmListagemDto>>('/api/cadastros/fiscal/ncm', {
        params: { busca, ativo, pagina, tamanhoPagina },
      })
      .then(r => r.data),

  obterPorCodigo: (codigo: string) =>
    apiClient.get<NcmDto>(`/api/cadastros/fiscal/ncm/${codigo}`).then(r => r.data),

  criar: (req: SalvarNcmRequest) =>
    apiClient.post<NcmDto>('/api/cadastros/fiscal/ncm', req).then(r => r.data),

  atualizar: (codigo: string, req: SalvarNcmRequest) =>
    apiClient.put(`/api/cadastros/fiscal/ncm/${codigo}`, req),

  excluir: (codigo: string) =>
    apiClient.delete(`/api/cadastros/fiscal/ncm/${codigo}`),

  importar: (arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return apiClient
      .post<ImportarNcmResultado>('/api/cadastros/fiscal/ncm/importar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data);
  },
};
